import React, { useState, useEffect } from 'react';
import { 
  Card, 
  Form, 
  Input, 
  Button, 
  DatePicker, 
  Select, 
  Upload, 
  message, 
  Typography, 
  Divider,
  Alert,
  InputNumber,
  Space,
  Steps
} from 'antd';
import { 
  UploadOutlined, 
  DollarOutlined, 
  FileDoneOutlined,
  CheckCircleOutlined,
  CreditCardOutlined,
  EditOutlined
} from '@ant-design/icons';
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import moment from 'moment';
import { manualPaymentService, ManualPaymentRequest } from '../../services/manualPaymentService';
import { useAuth } from '../../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import StripeCheckoutForm from '../../components/payments/StripeCheckoutForm';

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || 'pk_live_51RALVIJdaH5TMNaHtPCbNAya0IPskKnyPx9D7e8ZUmgBJQG6ZXBGcDp1ifgmPVG9iI0ikRUmQZWXm4MxcfE3OoB600txHNevf4');

const { Title, Text, Paragraph } = Typography;
const { Option } = Select;

interface BankInfo {
  bankName: string;
  accountName: string;
  iban: string;
  bic: string;
 // reference: string;
}

const ManualPaymentForm: React.FC = () => {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<string>('');
  const [paymentAmount, setPaymentAmount] = useState<number>(0);
  const [stripePaymentSuccess, setStripePaymentSuccess] = useState(false);
  const [bankInfo, setBankInfo] = useState<BankInfo>({
    bankName: 'Banque',
    accountName: '',
    iban: '',
    bic: '',
   // reference: 'AGENCY-' + Date.now()
  });
  const [fileList, setFileList] = useState<any[]>([]);
  const [submissionComplete, setSubmissionComplete] = useState(false);
  const { user } = useAuth();
  
  useEffect(() => {
    // Générer une référence unique au chargement
    const uniqueRef = `AGENCY-${user?.id || '000'}-${Date.now()}`;
    setBankInfo(prev => ({ ...prev, reference: uniqueRef }));
    form.setFieldsValue({ reference: uniqueRef });
  }, [form, user]);

  const handleSubmit = async (values: any) => {
    try {
      setLoading(true);
      /*console.log('Form values on submit:', values);
      console.log('amount :', paymentAmount);
      const paymentData: ManualPaymentRequest = {
        agencyId: user?.id || 0,
        amount: values.amount,
        paymentMethod: values.paymentMethod,
        reference: values.reference,
        paymentDate: values.paymentDate.format('YYYY-MM-DD'),
        description: values.description
      };
      
      if (fileList.length > 0 && fileList[0].originFileObj) {
        paymentData.attachmentFile = fileList[0].originFileObj;
      }*/
     const currentPaymentMethod = form.getFieldValue('paymentMethod') || selectedPaymentMethod;
      if(currentPaymentMethod !== "CARD") {
        const result = await manualPaymentService.requestBalanceTopUp(paymentAmount, values.description);
        message.success('Demande de paiement manuel soumise avec succès');
      }
      else{
        const result = await manualPaymentService.balanceStripeTopUp(values.paymentIntentId, values.id, values.reference);
        message.success('Paiement effectué avec succès');
      }
      
      setSubmissionComplete(true);
      setCurrentStep(getMaxSteps() - 1); // Aller à la dernière étape
    } catch (error) {
      console.error('Error submitting manual payment:', error);
      message.error('Erreur lors de la soumission du paiement manuel');
    } finally {
      setLoading(false);
    }
  };

  const handleStripeSuccess = (paymentIntentId: string, id:number) => {
    setStripePaymentSuccess(true);
    message.success('Paiement par carte réussi !');
    // Soumettre automatiquement le formulaire avec les données de carte
    const formValues = form.getFieldsValue();
    handleSubmit({
      ...formValues,
      paymentMethod: 'CARD',
      paymentIntentId: paymentIntentId,
      id: id
    });
  };

  const handleStripeError = (error: any) => {
    console.error('Stripe payment error:', error);
    message.error('Erreur lors du paiement par carte: ' + (error.message || 'Erreur inconnue'));
  };

  const getMaxSteps = () => {
    const method = form.getFieldValue('paymentMethod') || selectedPaymentMethod;
    return method === 'CARD' ? 4 : 3; // 4 étapes pour carte, 3 pour autres méthodes
  };

  const handleFileChange = (info: any) => {
    let newFileList = [...info.fileList];
    // Limiter à un seul fichier
    newFileList = newFileList.slice(-1);
    
    // Vérifier le type et la taille du fichier
    if (newFileList.length > 0) {
      const file = newFileList[0].originFileObj;
      const isValidType = file.type === 'application/pdf' || 
                         file.type.startsWith('image/');
      const isValidSize = file.size / 1024 / 1024 < 5; // max 5MB
      
      if (!isValidType) {
        message.error('Seuls les fichiers PDF et images sont acceptés');
        return;
      }
      
      if (!isValidSize) {
        message.error('La taille du fichier ne doit pas dépasser 5MB');
        return;
      }
    }
    
    setFileList(newFileList);
  };

  const nextStep = () => {
    form.validateFields(['amount', 'paymentMethod'])
      .then((values) => {
        setSelectedPaymentMethod(values.paymentMethod);
        setPaymentAmount(values.amount);
        setCurrentStep(currentStep + 1);
      })
      .catch(error => {
        console.log('Validation failed:', error);
      });
  };

  const prevStep = () => {
    setCurrentStep(currentStep - 1);
  };

  const uploadProps = {
    onRemove: () => {
      setFileList([]);
    },
    beforeUpload: (file: any) => {
      setFileList([file]);
      return false;
    },
    fileList,
  };

  const renderStep = () => {
    const currentPaymentMethod = form.getFieldValue('paymentMethod') || selectedPaymentMethod;
    
    switch (currentStep) {
      case 0:
        return (
          <>
            <Paragraph>
              {t('agency.manualPayment.amountMethodNote')}
            </Paragraph>
            
            <Form.Item
              name="amount"
              label={t('agency.manualPayment.amountLabel')}
              rules={[{ required: true, message: t('agency.manualPayment.amountRequired') }]}
            >
              <InputNumber
                style={{ width: '100%' }}
                min={1}
                step={0.01}
                formatter={value => `€ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
              />
            </Form.Item>
            
            <Form.Item
              name="paymentMethod"
              label={t('common.paymentMethod')}
              rules={[{ required: true, message: t('agency.manualPayment.methodRequired') }]}
            >
              <Select placeholder={t('agency.manualPayment.methodPlaceholder')}>
                <Option value="CASH">{t('paymentMethod.CASH')}</Option>
                {process.env.FEATURE_STRIPE && (
                <Option value="CARD">{t('agency.manualPayment.optionCard')}</Option>)}
                <Option value="OTHER">{t('paymentMethod.OTHER')}</Option>
              </Select>
            </Form.Item>
            
            <Form.Item>
              <Button type="primary" onClick={nextStep}>
                {t('common.continue')}
              </Button>
            </Form.Item>
          </>
        );
        
      case 1:
        // Si carte bancaire, afficher l'étape de paiement Stripe
        if (currentPaymentMethod === 'CARD' && process.env.FEATURE_STRIPE) {
          return (
            <>
              <Alert
                message={t('agency.manualPayment.cardPaymentTitle')}
                description={t('agency.manualPayment.cardPaymentDesc')}
                type="info"
                style={{ marginBottom: 24 }}
                showIcon
              />
              
              <Elements stripe={stripePromise}>
                <StripeCheckoutForm
                  amount={paymentAmount}
                  onSuccess={handleStripeSuccess}
                  onError={handleStripeError}
                />
              </Elements>
              
              <Form.Item style={{ marginTop: 24 }}>
                <Button onClick={prevStep}>
                  {t('common.back')}
                </Button>
              </Form.Item>
            </>
          );
        }
        
        // Pour les autres méthodes, afficher le formulaire de description
        return (
          <>
            <Form.Item
              name="description"
              label={t('common.description')}
              tooltip={t('agency.manualPayment.descriptionTooltip')}
            >
              <Input.TextArea rows={3} />
            </Form.Item>
            
            <Form.Item>
              <Space>
                <Button onClick={prevStep}>
                  {t('common.back')}
                </Button>
                <Button type="primary" htmlType="submit" loading={loading}>
                  {t('common.submit')}
                </Button>
              </Space>
            </Form.Item>
          </>
        );
        
      case 2:
        // Si carte bancaire, cette étape est pour la description après paiement réussi
        if (currentPaymentMethod === 'CARD' && process.env.FEATURE_STRIPE) {
          return (
            <>
              <Alert
                message={t('agency.manualPayment.paymentSuccessTitle')}
                description={t('agency.manualPayment.paymentSuccessDesc')}
                type="success"
                style={{ marginBottom: 24 }}
                showIcon
              />
              
              <Form.Item
                name="description"
                label={t('agency.manualPayment.descriptionOptional')}
                tooltip={t('agency.manualPayment.descriptionTooltip')}
              >
                <Input.TextArea rows={3} />
              </Form.Item>
              
              <Form.Item>
                <Space>
                  <Button onClick={prevStep}>
                    {t('common.back')}
                  </Button>
                  <Button type="primary" htmlType="submit" loading={loading}>
                    {t('agency.manualPayment.finalize')}
                  </Button>
                </Space>
              </Form.Item>
            </>
          );
        }
        
        // Pour les autres méthodes, c'est l'étape de confirmation finale
        return (
          <div style={{ textAlign: 'center', padding: '24px 0' }}>
            <CheckCircleOutlined style={{ fontSize: 64, color: '#52c41a', marginBottom: 24 }} />
            
            <Title level={3}>{t('agency.manualPayment.submittedTitle')}</Title>
            
            <Paragraph>
              {t('agency.manualPayment.submittedDesc')}
            </Paragraph>
            
            <Divider />
            
            {/*<Paragraph type="secondary">
              Référence de paiement: {form.getFieldValue('reference')}
            </Paragraph>*/}
            
            <Button type="primary" onClick={() => window.location.href = '/agency/dashboard'}>
              {t('agency.manualPayment.backToDashboard')}
            </Button>
          </div>
        );
        
      case 3:
        // Étape finale pour les paiements par carte
        return (
          <div style={{ textAlign: 'center', padding: '24px 0' }}>
            <CheckCircleOutlined style={{ fontSize: 64, color: '#52c41a', marginBottom: 24 }} />
            
            <Title level={3}>{t('agency.manualPayment.processedTitle')}</Title>
            
            <Paragraph>
              {t('agency.manualPayment.processedDesc')}
            </Paragraph>
            
            <Divider />
            
            <Paragraph type="secondary">
              Montant: {paymentAmount?.toFixed(2)} €<br />
              {t('paymentMethod.CARD')}<br />
              {/*Référence: {form.getFieldValue('reference')}*/}
            </Paragraph>
            
            <Button type="primary" onClick={() => window.location.href = '/agency/dashboard'}>
              {t('agency.manualPayment.backToDashboard')}
            </Button>
          </div>
        );
        
      default:
        return null;
    }
  };

  return (
    <Card title={t('agency.manualPayment.title')}>
      <Steps
        current={currentStep}
        style={{ marginBottom: 24 }}
        items={
          selectedPaymentMethod === 'CARD' ? [
            {
              title: t('agency.manualPayment.stepInfoTitle'),
              description: t('agency.manualPayment.stepInfoDesc'),
              icon: <CreditCardOutlined />
            },
            {
              title: t('agency.manualPayment.stepPaymentTitle'),
              description: t('agency.manualPayment.stepPaymentDesc'),
              icon: <CreditCardOutlined />
            },
            {
              title: t('agency.manualPayment.stepDescTitle'),
              description: t('agency.manualPayment.stepDescDesc'),
              icon: <EditOutlined />
            },
            {
              title: t('agency.manualPayment.stepConfirmTitle'),
              description: t('agency.manualPayment.stepConfirmDescCard'),
              icon: <CheckCircleOutlined />
            }
          ] : [
            {
              title: t('agency.manualPayment.stepInfoTitle'),
              description: t('agency.manualPayment.stepInfoDesc'),
              icon: <DollarOutlined />
            },
            {
              title: t('agency.manualPayment.stepDescTitle'),
              description: t('agency.manualPayment.stepDescDesc'),
              icon: <EditOutlined />
            },
            {
              title: t('agency.manualPayment.stepConfirmTitle'),
              description: t('agency.manualPayment.stepConfirmDescOther'),
              icon: <FileDoneOutlined />
            }
          ]
        }
      />
      
      <Form
        form={form}
        layout="vertical"
        initialValues={{
      //    reference: bankInfo.reference,
          paymentDate: moment(),
        }}
        onFinish={handleSubmit}
      >
        {renderStep()}
      </Form>
    </Card>
  );
};

export default ManualPaymentForm;
